import React from 'react';
import clsx from 'clsx';
import { Chart } from 'types/studio/studio';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { StudioBaseIconV2 } from 'shared/studio/StudioBaseIcon/StudioBaseIcon';
import { Typography, Space } from '@ag1/ui';
import { useUnfortunateWayToAppendCurrency } from 'state/hooks/useUnfortunateWayToAppendCurrency';
import styles from './ChartTable.module.scss';

type Props = {
  chart: NonNullable<Chart['chart']>;
  backgroundColor: string;
  textColor: string;
  colorColColor: string;
} & Pick<Chart, 'variation' | 'colorColIndex'>;

const ChartTable = ({
  chart,
  textColor,
  variation,
  colorColIndex,
  backgroundColor,
  colorColColor
}: Props) => {
  const { columnItems, rowItemsLabel, rowItems, caption } = chart;

  const { conditionallyAppendUSDtoSanityPrice } = useUnfortunateWayToAppendCurrency();

  return (
    <>
      <div className={styles.relative}>
        {variation === '2' && <div className={styles.shadow} />}
        <div className={clsx({ [styles.tableWrapper]: variation === '2' })}>
          <table className={styles.table} style={{ color: textColor }}>
            <thead
              className={clsx(
                variation === '1' && styles.headTable,
                variation === '2' && styles.headTable2
              )}>
              <tr>
                <th
                  className={clsx(styles.cellHead, {
                    [styles.cellHead2]: variation === '2'
                  })}
                  style={{ backgroundColor }}>
                  <div className={styles.cellContent}>
                    <Typography
                      variant="body"
                      size="2"
                      noPadding
                      className={styles.headText}
                      style={{ color: textColor }}>
                      {rowItemsLabel && <CustomPortableText value={rowItemsLabel} />}
                    </Typography>
                  </div>
                </th>
                {columnItems &&
                  columnItems.map(({ _key: key, label, icon }, index) => (
                    <th
                      key={key}
                      className={clsx(
                        styles.cellHead,
                        variation === '2' && styles.cellHead2,
                        colorColIndex && index === colorColIndex - 1 && styles.colorCellHead
                      )}
                      style={
                        colorColIndex && index === colorColIndex - 1
                          ? { backgroundColor: colorColColor }
                          : {}
                      }>
                      <div className={styles.cellContentCenter}>
                        {label && (
                          <div
                            className={clsx(
                              variation === '1' && styles.headText,
                              index === 0 && styles.paddingTop,
                              variation === '2' && styles.headText2
                            )}
                            style={{ color: textColor }}>
                            <CustomPortableText value={label} />
                          </div>
                        )}
                        {icon?.asset && <StudioBaseIconV2 icon={icon} className={styles.icon} />}
                      </div>
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {rowItems.map(({ _key: rowKey, label, values }) => (
                <tr
                  key={rowKey}
                  className={clsx(styles.row, {
                    [styles.row2]: variation === '2'
                  })}>
                  <td
                    className={clsx(styles.cellBody, {
                      [styles.cellBody2]: variation === '2'
                    })}
                    style={{ backgroundColor }}>
                    <div className={styles.cellContent}>
                      <div
                        className={clsx({
                          [styles.textItem]: variation === '1',
                          [styles.textItem2]: variation === '2'
                        })}
                        style={{ color: textColor }}>
                        <CustomPortableText value={label} />
                      </div>
                    </div>
                  </td>
                  {values &&
                    values.map(({ _key: cellKey, icon, text }, index) => (
                      <td
                        key={cellKey}
                        className={clsx(styles.cellBody, {
                          [styles.cellBody2]: variation === '2'
                        })}
                        style={
                          colorColIndex && index === colorColIndex - 1
                            ? { backgroundColor: colorColColor }
                            : {}
                        }>
                        <div className={styles.cellContentCenter}>
                          {icon?.asset && (
                            <StudioBaseIconV2
                              icon={icon}
                              className={clsx(styles.iconRow, {
                                [styles.iconRow2]: variation === '2'
                              })}
                            />
                          )}
                          {text && (
                            <div className={styles.text} style={{ color: textColor }}>
                              <CustomPortableText
                                value={conditionallyAppendUSDtoSanityPrice(text)}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {caption && (
        <Space top={{ xxs: 16 }}>
          <Typography
            variant="body"
            size="3"
            noPadding
            className={clsx({ [styles.caption]: variation === '2' })}
            style={{ color: textColor }}>
            <CustomPortableText value={caption} />
          </Typography>
        </Space>
      )}
    </>
  );
};

export default ChartTable;
